import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, LOCALE_ID, OnInit } from '@angular/core';
import { Account, AccountResource, AuditResource, BaseOrganizationEntity, Change } from '@apaq/orgkit-js';
import { container } from 'tsyringe';

@Component({
  selector: 'ws-audit-preview',
  templateUrl: './audit-preview.component.html',
  styleUrls: ['./audit-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuditPreviewComponent  {

  systemAccounts = ['admin', 'system', 'public', 'accounting'];
  _entity?: BaseOrganizationEntity;
  createdBy?: Account | null;
  lastModifiedBy?: Account | null;
  changes: Change[] = [];
  auditAccounts: Account[] = [];

  constructor(@Inject(LOCALE_ID) private locale: string, private changeDetector: ChangeDetectorRef,
  private accountResource: AccountResource,
  private auditResource: AuditResource) { }

  get entity() {
    return this._entity!;
  }

  @Input() 
  set entity(value: BaseOrganizationEntity) {
    this._entity = value;

    if(this._entity) {
      this.updateAccount();
      this.loadChanges();
    }
  }

  get createdDetails() {
    if(this.entity != null) {
      let name = this.createdBy?.name ?? this.entity.createdBy;
      return $localize`Created by ${name} at ${formatDate(this.entity.createdDate, 'short', this.locale)}`;
    } else {
      return '-';
    }
  }

  get updatedDetails() {
    if(this.entity != null) {
      let name = this.lastModifiedBy?.name ?? this.entity.lastModifiedBy;
      return $localize`Updated by ${name} at ${formatDate(this.entity.lastModifiedDate, 'short', this.locale)}`;
    } else {
      return '-';
    }
  }

  getAuditName(username: string) {
    return this.auditAccounts.find(a => a.login === username)?.name ?? username
  }

  private async loadChanges() {
    if(!this.entity) {
      this.changes = [];
    }

    this.changes = (await this.auditResource.findChanges(this.entity.id)).filter(c => this.isRelevantChangeInformation(c));
    
    var accountPromises: Promise<Account | null>[] = [];
    this.changes.forEach(c => {
      var username = c.commitMetadata.author;
      accountPromises.push(this.tryLoadUser(username));
    })

    this.auditAccounts = await Promise.all(accountPromises).then(result => result.filter(a => a!= null)) as Account[];
    this.changeDetector.detectChanges();
  }

  private isRelevantChangeInformation(change: Change) {
    var prop = change.propertyNameWithPath;

    // TODO: Should support all entities. This is currently only for assignments
    return change.left != null && ['accountId', 'plan.executionTime', 'action', 'status'].includes(prop);
  }

  private async updateAccount() {
    this.createdBy = await this.tryLoadUser(this.entity.createdBy);

    if(this.entity.createdBy === this.entity.lastModifiedBy) {
      this.lastModifiedBy = this.createdBy;
    } else {
      this.lastModifiedBy = await this.tryLoadUser(this.entity.lastModifiedBy);
    }
  }

  private async tryLoadUser(username: string) {
    if(this.systemAccounts.indexOf(username) >= 0) {
      return null;
    } else {
      return await this.accountResource.findById(username);
    }
  }

}
